import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import LinearProgress from '@mui/material/LinearProgress';
import { Pagination } from '../../../../model/pagination';
import { Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import PreviewIcon from '@mui/icons-material/Preview';
import SettingsIcon from '@mui/icons-material/Settings';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { IContractPagedResponse } from '../../../../model/scp/contract/response/contract-paged-response';
import ContractAppService from '../../../../application/platform/scp/contract/contract-app-service';
import { IContractPageRequest } from '../../../../model/scp/contract/request/contract-paged-request';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { dataGridLocaleConfig } from '../../../../config/data-grid-locale';

export interface IContractDataTableProps {
    parameters: IContractPageRequest;
    searchTrigger?: number;
};
interface IContractDataTableMenuContextProp {
    contract: IContractPagedResponse,
    loadData: (parameters: IContractPageRequest) => void
};
function MenuPopupState({ props }: { props: IContractDataTableMenuContextProp }) {
    const { showDialog, showMessage, showLoading } = useBaseLayout();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const gotToDetails = () => {
        handleClose();
        window.open(props.contract.url, '_blank');
    }

    return (
        <div>
            <IconButton
                color="primary"
                // variant="contained"
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}>
                <SettingsIcon />
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={gotToDetails}>
                    <ListItemIcon>
                        <PreviewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Acessar</ListItemText>
                </MenuItem>
                <MenuItem onClick={async () => {
                    handleClose();
                    navigate(`/contratos/publicar/${props.contract.id}`);
                }}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem onClick={async () => {
                    handleClose();
                    navigate(`/contratos/${props.contract.id}/aditivos`);
                }}>
                    <ListItemIcon>
                        <PostAddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Aditivos</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    showDialog({
                        title: 'Pergunta', message: 'Deseja realmente excluir o contrato? Caso deseje excluir preencha a justificativa abaixo:', onYesClick: (justification: string) => {
                            if (justification !== undefined && justification.length > 0) {
                                (async () => {
                                    showLoading({ open: true });
                                    await ContractAppService.deleteContract(justification, props.contract.id);
                                    props.loadData({} as IContractPageRequest);
                                    showLoading({ open: false });
                                    showMessage({ message: 'Contrato excluído com sucesso', type: 'success', open: true });
                                })();
                            } else {
                                showMessage({ message: 'Informe a justificativa para excluir o contrato', type: 'warning', open: true });
                            }
                        }, inputProps: {
                            rows: 4
                        }
                    } as DefaultDialogProps);

                }}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>
        </div>
    );
}

export default function ContractDataTable({ props }: { props: IContractDataTableProps }) {
    const [response, setResponse] = useState<Pagination<IContractPagedResponse>>(new Pagination<IContractPagedResponse>);
    const [purchaseDataTableProps, setPurchaseDataTableProps] = useState<IContractDataTableProps>(props);
    const [loadMoreData, setLoadMoreData] = useState(true);


    const loadData = (parameters: IContractPageRequest) => {
        (async () => {
            if (parameters !== undefined) {
                setLoadMoreData(true);
                const data = await ContractAppService.listContract(parameters);
                setResponse(data);
                setLoadMoreData(false);
            }
        })();
    }

    const columns: GridColDef<IContractPagedResponse>[] = [
        { field: 'id', headerName: 'Id', width: 90, sortable: false },
        {
            field: 'contractEffortNumber',
            headerName: 'Nº Empenho',
            flex: 1,
            minWidth: 140,
            sortable: false
        }, {
            field: 'numberProcess',
            headerName: 'Nº Processo',
            flex: 1,
            minWidth: 140,
            sortable: false
        },
        {
            field: 'year',
            headerName: 'Exercício',
            minWidth: 100,
            sortable: false
        },
        {
            field: 'supplierName',
            headerName: 'Fornecedor',
            flex: 1,
            minWidth: 160,
            sortable: false
        },
        {
            field: 'contractType',
            headerName: 'Tipo de Contrato',
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => {
                let chip = <Chip label={params.row.contractType} color="primary" />
                if (params.row.contractTypeId === 1) {
                    chip = <Chip label={params.row.contractType} color="primary" />
                } else if (params.row.contractTypeId === 2) {
                    chip = <Chip label={params.row.contractType} color="secondary" />
                } else if (params.row.contractTypeId === 3) {
                    chip = <Chip label={params.row.contractType} color="warning" />
                } else if (params.row.contractTypeId === 4) {
                    chip = <Chip label={params.row.contractType} color="error" />
                } else if (params.row.contractTypeId === 5) {
                    chip = <Chip label={params.row.contractType} color="success" />
                } else if (params.row.contractTypeId === 6) {
                    chip = <Chip label={params.row.contractType} color="default" />
                }
                return chip;
            }
        },
        {
            field: 'processCategory',
            headerName: 'Categoria de Processo',
            flex: 1,
            minWidth: 190,
            sortable: false,
            renderCell: (params) => {
                let chip = <Chip label={params.row.processCategory} color="primary" />
                if (params.row.categoryProcessId === 1) {
                    chip = <Chip label={params.row.processCategory} color="primary" />
                } else if (params.row.categoryProcessId === 2) {
                    chip = <Chip label={params.row.processCategory} color="secondary" />
                } else if (params.row.categoryProcessId === 3) {
                    chip = <Chip label={params.row.processCategory} color="warning" />
                } else if (params.row.categoryProcessId === 4) {
                    chip = <Chip label={params.row.processCategory} color="error" />
                } else if (params.row.categoryProcessId === 5) {
                    chip = <Chip label={params.row.processCategory} color="success" />
                } else if (params.row.categoryProcessId === 6) {
                    chip = <Chip label={params.row.processCategory} color="default" />
                }
                return chip;
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            align: "center",
            headerAlign: "center",
            sortable: false,
            renderCell: (params) => {
                return (
                    <MenuPopupState props={{ contract: params.row, loadData: loadData }}></MenuPopupState>

                );
            }
        }
    ];

    useEffect(() => {
        loadData(props.parameters);
        setPurchaseDataTableProps(props);
    }, [props.parameters.page, props.searchTrigger]); // Re-executa quando página ou searchTrigger mudarem

    return (
        <React.Fragment>
            <DataGrid
                sx={{ width: '100%', minWidth: 0 }}
                rows={response.items}
                columns={columns}
                columnVisibilityModel={{ id: false }}
                paginationModel={{ page: purchaseDataTableProps?.parameters.page ?? 0, pageSize: response.pageSize }}
                rowCount={response.totalRecords}
                disableRowSelectionOnClick
                onPaginationModelChange={(model) => {
                    const newParameters = { ...purchaseDataTableProps.parameters, page: model.page };
                    setPurchaseDataTableProps({ ...purchaseDataTableProps, parameters: newParameters });
                    loadData(newParameters);
                }}
                paginationMode="server"
                components={{
                    LoadingOverlay: LinearProgress,
                }}
                loading={loadMoreData}
                pageSizeOptions={[response.pageSize]}
                localeText={dataGridLocaleConfig}
            />
        </React.Fragment>
    );
}